import { getConnectivityOnline, setConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { clearOfflineData } from "@/domains/offline/db/mivoDb";
import { cacheAuthUser, getCachedAuthUser } from "@/domains/offline/repositories/authRepository";
import { authApi } from "@/domains/shared/apis/authApi";
import { legalStatusQueryKey } from "@/domains/shared/hooks/useLegalStatus";
import type { IUser } from "@/domains/shared/models/IUser";
import type { RegisterPayload } from "@/domains/shared/models/model";
import type { AuthResult } from "@/domains/shared/models/TAuthResult";
import { formatApiError } from "@/domains/shared/services/api";
import { clearSeenDeployVersion } from "@/domains/shared/services/deployVersion";
import { isPublicAuthPath } from "@/domains/shared/services/sessionExpired";
import { resetCsrfToken } from "@/domains/shared/services/setupCsrfInterceptor";
import { getBrowserTimeZoneId } from "@/domains/shared/utils/detectTimeZone";
import { syncPreferredLocaleFromUser } from "@/domains/shared/utils/syncPreferredLocale";
import { setPlanIntent } from "@/domains/subscription/planIntent";
import { isPaidPlan, type SubscriptionPlanId } from "@/domains/subscription/plans";
import { identifyProductUser, resetProductAnalytics, captureProductEvent } from "@/monitoring/productAnalytics";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";

export const authMeQueryKey = ["auth", "me"] as const;

type LoginChallengeResponse = {
    detail?: unknown;
    code?: unknown;
    reason?: unknown;
    reactivationToken?: unknown;
    email?: unknown;
    maskedEmail?: unknown;
    trialEndsAt?: unknown;
};

async function syncTimeZoneIfNeeded(user: IUser): Promise<IUser> {
    const browserTz = getBrowserTimeZoneId();
    if (user.timeZoneId === browserTz) {
        return user;
    }

    try {
        const { data } = await authApi.patchMe({ timeZoneId: browserTz });
        await cacheAuthUser(data.user);
        return data.user;
    } catch {
        return user;
    }
}

async function syncPlanIntentFromUser(user: IUser): Promise<IUser> {
    if (user.pendingPaidPlanId && isPaidPlan(user.pendingPaidPlanId)) {
        setPlanIntent(user.pendingPaidPlanId as SubscriptionPlanId);
    }
    return user;
}

async function fetchMe(): Promise<IUser | null> {
    try {
        const { data } = await authApi.me();
        const synced = syncPreferredLocaleFromUser(await syncTimeZoneIfNeeded(data.user));
        await syncPlanIntentFromUser(synced);
        await cacheAuthUser(synced);
        setConnectivityOnline(true);
        identifyProductUser(synced);
        return synced;
    } catch {
        if (!getConnectivityOnline()) {
            const cached = await getCachedAuthUser();
            if (cached) {
                return cached;
            }
        }
        return null;
    }
}

export function useAuth() {
    const queryClient = useQueryClient();
    const { pathname } = useLocation();
    const sessionProbeEnabled = !isPublicAuthPath(pathname);

    const meQuery = useQuery({
        queryKey: authMeQueryKey,
        queryFn: fetchMe,
        retry: false,
        staleTime: 60_000,
        enabled: sessionProbeEnabled,
    });

    const login = useCallback(
        async (email: string, password: string): Promise<AuthResult> => {
            try {
                const { data } = await authApi.login(email, password);
                const synced = syncPreferredLocaleFromUser(await syncTimeZoneIfNeeded(data.user));
                await syncPlanIntentFromUser(synced);
                resetCsrfToken();
                queryClient.setQueryData(authMeQueryKey, synced);
                setConnectivityOnline(true);
                identifyProductUser(synced);
                return { ok: true, user: synced };
            } catch (e: unknown) {
                const ax = e as { response?: { data?: LoginChallengeResponse }; message?: string };
                const payload = ax.response?.data;
                const detail =
                    formatApiError(payload?.detail, {
                        code: typeof payload?.code === "string" ? payload.code : null,
                    }) ||
                    ax.message ||
                    "Login failed";

                if (
                    payload?.reason === "reactivation_required" &&
                    typeof payload.reactivationToken === "string" &&
                    typeof payload.email === "string" &&
                    typeof payload.maskedEmail === "string" &&
                    typeof payload.trialEndsAt === "string"
                ) {
                    return {
                        ok: false,
                        error: detail,
                        reason: "reactivation_required",
                        reactivationToken: payload.reactivationToken,
                        email: payload.email,
                        maskedEmail: payload.maskedEmail,
                        trialEndsAt: payload.trialEndsAt,
                    };
                }

                if (
                    payload?.reason === "paid_plan_required" &&
                    typeof payload.email === "string" &&
                    typeof payload.trialEndsAt === "string"
                ) {
                    return {
                        ok: false,
                        error: detail,
                        reason: "paid_plan_required",
                        email: payload.email,
                        trialEndsAt: payload.trialEndsAt,
                    };
                }

                return {
                    ok: false,
                    error: detail,
                };
            }
        },
        [queryClient],
    );

    const register = useCallback(
        async (payload: RegisterPayload): Promise<AuthResult> => {
            try {
                const { data } = await authApi.registerProfessional(payload);
                const synced = syncPreferredLocaleFromUser(data.user);
                await syncPlanIntentFromUser(synced);
                resetCsrfToken();
                queryClient.setQueryData(authMeQueryKey, synced);
                setConnectivityOnline(true);
                identifyProductUser(synced);
                return { ok: true, user: synced };
            } catch (e: unknown) {
                const ax = e as { response?: { data?: { detail?: unknown; code?: unknown } }; message?: string };
                return {
                    ok: false,
                    error:
                        formatApiError(ax.response?.data?.detail, {
                            code: typeof ax.response?.data?.code === "string" ? ax.response.data.code : null,
                        }) ||
                        ax.message ||
                        "Registration failed",
                };
            }
        },
        [queryClient],
    );

    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch {
            /* ignore */
        }
        captureProductEvent("auth_logout");
        resetCsrfToken();
        clearSeenDeployVersion();
        await clearOfflineData();
        queryClient.removeQueries({ queryKey: authMeQueryKey });
        queryClient.removeQueries({ queryKey: legalStatusQueryKey });
        queryClient.removeQueries({ queryKey: ["billing", "change-plan", "secretary-candidates"] });
        resetProductAnalytics();
    }, [queryClient]);

    const applyAuthUser = useCallback(
        (user: IUser) => {
            resetCsrfToken();
            queryClient.setQueryData(authMeQueryKey, user);
            setConnectivityOnline(true);
            void cacheAuthUser(user);
            identifyProductUser(user);
        },
        [queryClient],
    );

    const refreshMe = useCallback(async () => {
        if (!sessionProbeEnabled) {
            return queryClient.getQueryData<IUser | null>(authMeQueryKey) ?? null;
        }

        return queryClient.fetchQuery({ queryKey: authMeQueryKey, queryFn: fetchMe });
    }, [queryClient, sessionProbeEnabled]);

    const user = sessionProbeEnabled ? (meQuery.data ?? null) : null;
    const loading = sessionProbeEnabled ? meQuery.isPending : false;

    return useMemo(
        () => ({
            user,
            loading,
            login,
            register,
            logout,
            applyAuthUser,
            refreshMe,
        }),
        [user, loading, login, register, logout, applyAuthUser, refreshMe],
    );
}
